// src/components/common/AppErrorFallback.tsx
import { SUPPORT_URL } from '../../constants';
import './AppErrorFallback.css';

interface AppErrorFallbackProps {
  onReset?: () => void;
}

/**
 * Fallback do ErrorBoundary de topo (issue #22). Renderiza apenas quando o
 * React lança na fase de commit — cenário clássico de DOM mutado por extensão
 * de tradução do navegador (Google Translate envolve text nodes em <font>,
 * quebrando as referências do React → removeChild/insertBefore NotFoundError).
 *
 * Recuperação via window.location.reload(): um re-render simples pode recair
 * sobre o mesmo DOM já mutado; o hard-reload reconstrói a página limpa (mesmo
 * padrão defensivo da premissa #5 usado em logout/401).
 *
 * Deliberadamente SEM dependência de contexto React (tema via CSS vars globais
 * [data-theme]) nem de ícones/barrels: um fallback de erro precisa renderizar
 * mesmo quando a árvore de providers/render está comprometida. O translate="no"
 * evita que a própria extensão de tradução mute este fallback e o derrube de novo.
 * `SUPPORT_URL` respeita essa restrição: `constants/index.ts` não importa nada,
 * é literal inlined no build — não há leitura em runtime que possa falhar aqui.
 */
const AppErrorFallback = ({ onReset }: AppErrorFallbackProps) => {
  const handleReload = () => {
    onReset?.();
    window.location.reload();
  };

  return (
    <div className="app-error-fallback" role="alert" translate="no">
      <h1 className="app-error-fallback-title">Algo deu errado</h1>
      <p className="app-error-fallback-text">
        Ocorreu um erro inesperado ao exibir esta tela. Recarregue a página para continuar.
      </p>
      <p className="app-error-fallback-hint">
        Se você estiver usando tradução automática do navegador nesta página,
        desative-a para evitar que o erro se repita.
      </p>
      <button
        type="button"
        className="app-error-fallback-button"
        onClick={handleReload}
      >
        Recarregar página
      </button>
      {/* Saída para quando recarregar não resolve. Abre em nova aba: a página
          está em estado de erro e navegar na mesma aba descartaria o contexto
          que o usuário talvez precise relatar. */}
      <p className="app-error-fallback-support">
        Se o problema persistir, contate o{' '}
        <a
          className="app-error-fallback-link"
          href={SUPPORT_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          Suporte
        </a>
        .
      </p>
    </div>
  );
};

export default AppErrorFallback;
